import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = process.env.REACT_APP_SITE_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_content-watch-7/artifacts/thy81wjj_747B42A6-4C25-4A17-A557-B9BBE91C99DB%20Background%20Removed.png";
const DEFAULT_SHARE_IMAGE = `${SITE_URL}/apple-touch-icon.png`;

const ensureMetaTag = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
};

const ensureLinkTag = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
};

export const usePageSeo = ({ canonicalPath, description, image, robots = "index,follow,max-image-preview:large", schema = [], title, type = "website" }) => {
  const location = useLocation();

  useEffect(() => {
    const canonicalUrl = `${SITE_URL}${canonicalPath || location.pathname}`;
    const shareImage = image || DEFAULT_SHARE_IMAGE;
    const schemaId = "content-watch-seo-schema";

    document.title = title;
    ensureMetaTag('meta[name="description"]', { name: "description", content: description });
    ensureMetaTag('meta[name="robots"]', { name: "robots", content: robots });
    ensureMetaTag('meta[property="og:title"]', { property: "og:title", content: title });
    ensureMetaTag('meta[property="og:description"]', { property: "og:description", content: description });
    ensureMetaTag('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    ensureMetaTag('meta[property="og:type"]', { property: "og:type", content: type });
    ensureMetaTag('meta[property="og:site_name"]', { property: "og:site_name", content: "Content Watch HQ" });
    ensureMetaTag('meta[property="og:image"]', { property: "og:image", content: shareImage });
    ensureMetaTag('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    ensureMetaTag('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    ensureMetaTag('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    ensureMetaTag('meta[name="twitter:image"]', { name: "twitter:image", content: shareImage });
    ensureLinkTag('link[rel="canonical"]', { rel: "canonical", href: canonicalUrl });

    const existingScript = document.getElementById(schemaId);
    if (existingScript) {
      existingScript.remove();
    }

    if (schema.length) {
      const script = document.createElement("script");
      script.id = schemaId;
      script.type = "application/ld+json";
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    }
  }, [canonicalPath, description, image, location.pathname, robots, schema, title, type]);
};

export const buildSiteSchemas = () => ([
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Content Watch HQ",
    url: SITE_URL,
      logo: LOGO_URL,
    sameAs: [
      "https://www.instagram.com/contentwatchhq/",
      "https://www.youtube.com/@CONTENTWATCHhq",
      "https://www.facebook.com/contentwatchhq/",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "News tips",
      email: "info@contentwatchhq.com",
      areaServed: "Houston, Texas",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Content Watch HQ",
    url: SITE_URL,
    description: "Content Watch HQ delivers Houston breaking news, public safety coverage, investigations, public records, live video updates, and accountability reporting across Southeast Texas.",
    publisher: {
      "@type": "Organization",
      name: "Content Watch HQ",
    },
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  },
]);